import React from 'react';
import { FormattedNumber } from 'react-intl';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchPost } from '../../../store/fetch';
import { PanelBody, PanelHeading, PanelLink, PopupElement, Table, TableBody, TableData, TableHead, TableHeader, TableRow } from '../../elements';
import { IMaintenanceMeasure } from '../../interfaces/IMaintenanceMeasure';
import { MaintenancePopupDetails } from '../../MaintenanceMeasures/MaintenancePopupDetails';
import { ConditionScoreLabel } from '../../utils/ConditionScoreLabel';
import { FmecaRiskLabel } from '../../utils/FmecaRiskLabel';
import { RamsWeightedPrioLabel } from '../../utils/RamsWeightedPrioLabel';

function FormatConditionLabel(props: {
    score?: number;
    ramsWeightedPriority?: string;
    analysisRamsWeightedPriority?: string;
    verificationRamsWeightedPriority?: string;
}) {
    if (props === undefined) {
        return null;
    }
    if (props.ramsWeightedPriority && props.ramsWeightedPriority !== '0') {
        return <RamsWeightedPrioLabel value={props.ramsWeightedPriority} />;
    }
    if (props.verificationRamsWeightedPriority && props.verificationRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.verificationRamsWeightedPriority} />;
    }
    if (props.analysisRamsWeightedPriority && props.analysisRamsWeightedPriority !== '0') {
        return <FmecaRiskLabel weightedValue={props.analysisRamsWeightedPriority} />;
    }
    if (props.score) {
        return <ConditionScoreLabel value={props.score} />;
    }
    return null;
}

export function MaintenanceListNenFemca(props: {
    surveyId?: string | undefined;
    inspectionStandardType?: string | undefined;
    maintenanceMeasures: IMaintenanceMeasure[];
    onChanged: () => Promise<void>;
}) {
    const translate = useTranslate();

    const [selectedMaintenance, setSelectedMaintenance] = React.useState<IMaintenanceMeasure | undefined>();

    async function setImplementationYear(maintenanceMeasureId: string) {
        setSelectedMaintenance(undefined);

        const data = {
            implementationYear: new Date().getFullYear(),
        };
        await fetchPost(`/api/maintenancemeasures/${maintenanceMeasureId}`, {}, data);
        await props.onChanged();
    }

    return (
        <div className="panel" id="Maintenance">
            <PanelHeading text={translate('maintenanceMeasure.maintenanceMeasures')} icon="fa fa-wrench">
                <PanelLink
                    href={`/api/surveys/${props.surveyId}/miokExcelSurvey?inspectionType=${props.inspectionStandardType}`}
                    text="MJOP export"
                    isDownload={true}
                    icon="fa fa-download"
                    small={true}
                />
            </PanelHeading>
            <PanelBody noPadding={true} whiteBackground={true}>
                <Table>
                    <TableHead>
                        <tr className="bg-light">
                            <TableHeader>{translate('common.location')}</TableHeader>
                            <TableHeader>{translate('maintenanceMeasure.maintenanceMeasure')}</TableHeader>
                            <TableHeader>{translate('common.cost')}</TableHeader>
                            <TableHeader>{translate('maintenanceMeasure.planned')}</TableHeader>
                            <TableHeader>{translate('maintenanceMeasure.completed')}</TableHeader>
                            <TableHeader>&nbsp;</TableHeader>
                        </tr>
                    </TableHead>
                    <TableBody>
                        {props.maintenanceMeasures.map((maintenance) => (
                            <TableRow key={maintenance.id} onClick={() => setSelectedMaintenance(maintenance)}>
                                {maintenance.unit && (
                                    <TableData>
                                        <span className="fs12">
                                            {maintenance.unit.element.name}
                                            <br />
                                            {maintenance.unit.name}
                                        </span>
                                    </TableData>
                                )}
                                {maintenance.manifestation && (
                                    <TableData>
                                        <span className="fs12">
                                            {maintenance.manifestation.unit.element.name}
                                            <br />
                                            {maintenance.manifestation.unit.name}
                                            <br />
                                            {maintenance.manifestation.name}
                                        </span>
                                    </TableData>
                                )}
                                <TableData>
                                    {maintenance.description}
                                    {(maintenance.maintenanceType && (
                                        <div className="fs12 text-muted-darker">{maintenance.maintenanceType} </div>
                                    )) || <div className="fs12 text-muted-darker">{translate('maintenanceMeasure.correctiveMaintenance')} </div>}
                                </TableData>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <FormattedNumber value={maintenance.cost ?? 0} style="currency" currency="EUR" />{' '}
                                </td>
                                <td className="fs12" style={{ whiteSpace: 'nowrap' }}>
                                    {maintenance.planYear} {maintenance.finalPlanYear && <span>-{maintenance.finalPlanYear}</span>}
                                </td>
                                <TableData>
                                    <span className="fs12">{maintenance.implementationYear} </span>
                                </TableData>
                                <TableData alignRight={true}>
                                    <FormatConditionLabel {...{ ...maintenance.defect, ...maintenance.failureMode }}></FormatConditionLabel>
                                </TableData>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PanelBody>
            {selectedMaintenance && (
                <>
                    <PopupElement onClick={() => setSelectedMaintenance(undefined)}>
                        <MaintenancePopupDetails
                            maintenanceMeasure={selectedMaintenance}
                            setImplementationYear={() => {
                                void setImplementationYear(selectedMaintenance.id);
                            }}
                            onClose={() => setSelectedMaintenance(undefined)}
                        />
                    </PopupElement>
                </>
            )}
        </div>
    );
}
